import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2, Mail, Stethoscope, HelpCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { isValidEmailFormat, suggestEmailCorrection } from "../utils/emailValidation";
import { useTenant, tenantBranding } from "@/hooks/useTenant";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PrimeraVez = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const brand = tenantBranding(tenant);
  const [view, setView] = useState("menu"); // 'menu' | 'request' | 'success'
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    patient_name: "",
    patient_email: "",
    patient_dni: "",
    doctor_name: "",
    doctor_email: "",
    message: "",
  });

  const canSubmit =
    formData.patient_name.trim() &&
    isValidEmailFormat(formData.patient_email) &&
    formData.patient_dni.trim() &&
    formData.doctor_name.trim() &&
    isValidEmailFormat(formData.doctor_email) &&
    formData.patient_email.trim().toLowerCase() !== formData.doctor_email.trim().toLowerCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patients/request-doctor-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setView("success");
      } else {
        toast.error(data.detail || "No pudimos enviar tu solicitud. Probá de nuevo.");
      }
    } catch {
      toast.error("Error de conexión. Probá de nuevo en un momento.");
    } finally {
      setSubmitting(false);
    }
  };

  const patientEmailSuggestion = formData.patient_email
    ? suggestEmailCorrection(formData.patient_email)
    : null;
  const doctorEmailSuggestion = formData.doctor_email
    ? suggestEmailCorrection(formData.doctor_email)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="primera-vez-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Volver
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.name} className="h-8 sm:h-10 object-contain" />
            ) : (
              <span
                className="text-lg font-semibold tracking-tight"
                style={{ color: brand.primaryColor, fontFamily: "'Manrope', sans-serif" }}
              >
                {brand.name}
              </span>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {view === "menu" && (
          <div data-testid="primera-vez-menu">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              ¿Es tu primera consulta?
            </h1>
            <p className="text-slate-600 mb-8">
              Para iniciar una consulta necesitás el enlace personal de tu médico. Elegí la opción que mejor describe tu situación.
            </p>

            <div className="space-y-3">
              {/* Option 1: already has the link */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4">
                <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">Mi médico ya me envió un enlace</h3>
                  <p className="text-sm text-slate-600">
                    Buscá el link en tu WhatsApp, email o SMS. Hacé click ahí para iniciar tu consulta.
                  </p>
                </div>
              </div>

              {/* Option 2: request link from doctor */}
              <button
                type="button"
                onClick={() => setView("request")}
                data-testid="open-request-form-btn"
                className="w-full text-left bg-white border-2 border-blue-200 hover:border-blue-400 rounded-xl p-5 flex items-start gap-4 transition-colors"
              >
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Send className="h-5 w-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                    Pedirle el enlace a mi médico
                    <span className="text-[10px] uppercase bg-blue-600 text-white rounded px-1.5 py-0.5">Recomendado</span>
                  </h3>
                  <p className="text-sm text-slate-600">
                    Le enviamos un email a tu médico de tu parte para que te comparta su enlace personal.
                  </p>
                </div>
              </button>

              {/* Option 3: no doctor */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4">
                <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">No tengo médico todavía</h3>
                  <p className="text-sm text-slate-600">
                    Contactá al centro de turnos de Fleni al{" "}
                    <a href="tel:+541157773200" className="text-blue-600 font-medium">+54 11 5777-3200</a>
                    {" "}o por WhatsApp al{" "}
                    <a href="https://wa.me/5491156551155" target="_blank" rel="noreferrer" className="text-blue-600 font-medium">
                      +54 9 11 5655-1155
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {view === "request" && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="request-form">
            <Button variant="ghost" size="sm" onClick={() => setView("menu")} type="button">
              <ArrowLeft className="h-4 w-4 mr-1" /> Atrás
            </Button>

            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                Pedirle el enlace a mi médico
              </h1>
              <p className="text-slate-600">
                Le vamos a enviar un email de tu parte. Si tu médico ya usa Thales Health, va a recibir tu solicitud directamente. Si todavía no usa la plataforma, le enviaremos una invitación.
              </p>
            </div>

            {/* Tus datos */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                Tus datos (paciente)
              </h3>
              <div>
                <Label htmlFor="patient_name">Tu nombre completo *</Label>
                <Input
                  id="patient_name"
                  data-testid="req-patient-name"
                  value={formData.patient_name}
                  onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                  placeholder="Ej: Juan Pérez"
                  className="h-11 mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="patient_email">Tu email *</Label>
                <Input
                  id="patient_email"
                  type="email"
                  data-testid="req-patient-email"
                  value={formData.patient_email}
                  onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                  placeholder="tu@email.com"
                  className="h-11 mt-1"
                  required
                />
                {patientEmailSuggestion && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, patient_email: patientEmailSuggestion })}
                    className="mt-1 text-xs text-amber-700 hover:underline"
                  >
                    ¿Quisiste decir <strong>{patientEmailSuggestion}</strong>?
                  </button>
                )}
              </div>
              <div>
                <Label htmlFor="patient_dni">Tu DNI *</Label>
                <Input
                  id="patient_dni"
                  data-testid="req-patient-dni"
                  value={formData.patient_dni}
                  onChange={(e) => setFormData({ ...formData, patient_dni: e.target.value.replace(/\D/g, "") })}
                  placeholder="Sin puntos"
                  inputMode="numeric"
                  className="h-11 mt-1"
                  required
                />
              </div>
            </div>

            {/* Datos del médico */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-blue-600" />
                Datos del médico
              </h3>
              <div>
                <Label htmlFor="doctor_name">Nombre del médico *</Label>
                <Input
                  id="doctor_name"
                  data-testid="req-doctor-name"
                  value={formData.doctor_name}
                  onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                  placeholder="Ej: Dr. Francisco Varela"
                  className="h-11 mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="doctor_email">Email del médico *</Label>
                <Input
                  id="doctor_email"
                  type="email"
                  data-testid="req-doctor-email"
                  value={formData.doctor_email}
                  onChange={(e) => setFormData({ ...formData, doctor_email: e.target.value })}
                  placeholder="doctor@institucion.com"
                  className="h-11 mt-1"
                  required
                />
                {doctorEmailSuggestion && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, doctor_email: doctorEmailSuggestion })}
                    className="mt-1 text-xs text-amber-700 hover:underline"
                  >
                    ¿Quisiste decir <strong>{doctorEmailSuggestion}</strong>?
                  </button>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Asegurate de que sea el email correcto, le va a llegar tu solicitud ahí.
                </p>
              </div>
              <div>
                <Label htmlFor="message">Mensaje para tu médico (opcional)</Label>
                <Textarea
                  id="message"
                  data-testid="req-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 300) })}
                  placeholder="Ej: Hola doctor, quería iniciar una consulta sobre mi tratamiento reciente."
                  rows={3}
                  className="mt-1"
                  maxLength={300}
                />
                <p className="text-xs text-slate-400 mt-1">
                  {formData.message.length}/300
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <strong>Importante:</strong> Solo enviamos esta solicitud al médico que indicaste, una sola vez. Tus datos quedan registrados para auditoría. Si no recibís respuesta en 48hs, tu médico puede no usar la plataforma todavía.
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              data-testid="submit-request-btn"
              className="w-full h-12 bg-[#1d366b] hover:bg-[#152a52] text-white"
            >
              {submitting ? "Enviando..." : "Enviar solicitud"}
              <Send className="ml-2 h-4 w-4" />
            </Button>
          </form>
        )}

        {view === "success" && (
          <div data-testid="success-view" className="text-center bg-white border border-slate-200 rounded-xl p-8">
            <div className="h-16 w-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Solicitud enviada</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Le enviamos un email a <strong>{formData.doctor_name}</strong> ({formData.doctor_email}) con tu solicitud.
              {" "}Cuando responda, vas a recibir tu enlace personal en tu casilla.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2.5 mb-6 text-left" data-testid="spam-alert">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>¿No te llegó?</strong> Revisá tu carpeta de <strong>spam o correo no deseado</strong>.
              </p>
            </div>
            <Button onClick={() => navigate("/")} variant="outline" data-testid="success-home-btn">
              Volver al inicio
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrimeraVez;
